class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
    this.startInjectedWidgetObserver();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.queueInjectedWidgetEnhancement();

    // A short delay makes the transform transition reliable after AJAX section rendering.
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.queueInjectedWidgetEnhancement();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.queueInjectedWidgetEnhancement();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  startInjectedWidgetObserver() {
    this.injectedWidgetObserver = new MutationObserver(() => this.queueInjectedWidgetEnhancement());
    this.injectedWidgetObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-pressed', 'aria-selected', 'checked'],
    });
    this.queueInjectedWidgetEnhancement();
  }

  queueInjectedWidgetEnhancement() {
    window.clearTimeout(this.injectedWidgetTimer);
    this.injectedWidgetTimer = window.setTimeout(() => this.enhanceInjectedWidgets(), 80);
  }

  enhanceInjectedWidgets() {
    const footer = this.querySelector('.drawer__footer');
    if (!footer) return;

    const deliveryIntro = footer.querySelector('#IrieCartDeliveryIntro');
    const widgetRoot = this.findPickeasyWidgetRoot(footer);

    if (!widgetRoot) return;

    widgetRoot.classList.add('irie-pickeasy-widget');
    widgetRoot.setAttribute('data-irie-cart-widget', 'pickeasy');
    widgetRoot.setAttribute('role', widgetRoot.getAttribute('role') || 'region');
    widgetRoot.setAttribute('aria-label', widgetRoot.getAttribute('aria-label') || 'Delivery or collection options');
    this.decoratePickeasyWidget(widgetRoot);

    // Pickeasy normally injects immediately before checkout. Keep our heading attached
    // to the widget even after cart AJAX refreshes, without moving or cloning app fields.
    if (deliveryIntro && deliveryIntro.parentElement === footer && widgetRoot.parentElement === footer) {
      if (deliveryIntro.nextElementSibling !== widgetRoot) widgetRoot.before(deliveryIntro);
    }
  }


  decoratePickeasyWidget(widgetRoot) {
    if (!widgetRoot) return;

    const normalise = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const findTextElement = (text) => {
      const target = normalise(text);
      const candidates = Array.from(
        widgetRoot.querySelectorAll('button, [role="button"], label, div, span, p, strong, b')
      )
        .filter((element) => {
          const content = normalise(element.textContent);
          return content === target || (content.includes(target) && content.length <= target.length + 36);
        })
        .sort((a, b) => {
          const interactiveA = a.matches('button, [role="button"], label') ? 0 : 1;
          const interactiveB = b.matches('button, [role="button"], label') ? 0 : 1;
          return interactiveA - interactiveB || normalise(a.textContent).length - normalise(b.textContent).length;
        });

      return candidates[0] || null;
    };

    const getControl = (element) => {
      if (!element) return null;
      const interactive = element.closest('button, [role="button"], label');
      if (interactive && widgetRoot.contains(interactive)) return interactive;

      const parent = element.parentElement;
      if (parent && parent !== widgetRoot && parent.children.length <= 4) return parent;
      return element;
    };

    const pickupControl = getControl(findTextElement('Store Pickup'));
    const shippingControl = getControl(findTextElement('Standard Shipping'));
    const methodControls = [pickupControl, shippingControl].filter(Boolean);

    methodControls.forEach((control) => control.classList.add('irie-pickeasy-method'));

    // Pickeasy adds the date and notes fields after a fulfilment method is selected.
    // Re-evaluate the methods wrapper on every injection/update so a former two-button
    // grid can never trap the newly inserted fields in narrow columns.
    let methodsContainer = null;

    if (methodControls.length === 2) {
      let commonParent = methodControls[0].parentElement;
      while (commonParent && commonParent !== widgetRoot && !commonParent.contains(methodControls[1])) {
        commonParent = commonParent.parentElement;
      }

      const commonText = normalise(commonParent?.textContent);
      const containsFulfilmentFields = Boolean(
        commonParent?.querySelector(
          'textarea, select, input[type="date"], input[placeholder*="date" i], [class*="date-picker" i], [class*="additional" i]'
        )
      );

      if (
        commonParent &&
        commonParent !== widgetRoot &&
        !containsFulfilmentFields &&
        !commonText.includes('when would you like') &&
        !commonText.includes('additional details')
      ) {
        methodsContainer = commonParent;
      }
    }

    widgetRoot.querySelectorAll('.irie-pickeasy-methods').forEach((container) => {
      if (container !== methodsContainer) container.classList.remove('irie-pickeasy-methods');
    });
    methodsContainer?.classList.add('irie-pickeasy-methods');

    methodControls.forEach((control) => {
      const stateText = `${control.className || ''} ${control.getAttribute('aria-pressed') || ''} ${control.getAttribute('aria-selected') || ''}`.toLowerCase();
      const checkedInput = control.querySelector('input:checked');
      control.classList.toggle(
        'is-selected',
        Boolean(checkedInput) || /(^|\s)(active|selected|checked|current)(\s|$)/.test(stateText) || control.getAttribute('aria-pressed') === 'true' || control.getAttribute('aria-selected') === 'true'
      );
    });

    const addressMarker = findTextElement('South Woodford') || findTextElement('E18 1AY');
    if (addressMarker) {
      const addressCard = getControl(addressMarker);
      addressCard?.classList.add('irie-pickeasy-location');
    }

    const question = findTextElement('When would you like to get this order?');
    question?.classList.add('irie-pickeasy-question');

    const additionalLabel = findTextElement('Additional Details');
    additionalLabel?.classList.add('irie-pickeasy-additional-label');

    widgetRoot.querySelectorAll('input, select').forEach((field) => {
      const placeholder = normalise(field.getAttribute('placeholder'));
      if (field.type === 'date' || placeholder.includes('select date') || placeholder.includes('choose date')) {
        field.classList.add('irie-pickeasy-date-field');
      }
    });

    widgetRoot.querySelectorAll('textarea').forEach((field) => field.classList.add('irie-pickeasy-notes-field'));

    if (!widgetRoot.dataset.iriePickeasyBound) {
      widgetRoot.dataset.iriePickeasyBound = 'true';
      widgetRoot.addEventListener('click', () => {
        window.setTimeout(() => this.queueInjectedWidgetEnhancement(), 30);
      });
      widgetRoot.addEventListener('change', () => {
        window.setTimeout(() => this.queueInjectedWidgetEnhancement(), 30);
      });
    }
  }

  findPickeasyWidgetRoot(footer) {
    const knownSelectors = [
      '[id*="pickeasy"]',
      '[class*="pickeasy"]',
      '[id*="logbase"]',
      '[class*="logbase"]',
      '[id*="pickup-delivery"]',
      '[class*="pickup-delivery"]',
      '[id*="delivery-date"]',
      '[class*="delivery-date"]',
    ];

    for (const selector of knownSelectors) {
      const match = footer.querySelector(selector);
      const root = this.getDirectFooterChild(match, footer);
      if (root && this.isEligibleWidgetRoot(root)) return root;
    }

    const markers = [
      'store pickup',
      'standard shipping',
      'when would you like to get this order',
      'additional details',
      'select date',
    ];

    const candidates = Array.from(footer.querySelectorAll('div, section, form, fieldset'))
      .filter((element) => this.isEligibleWidgetRoot(element))
      .map((element) => {
        const text = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const score = markers.reduce((total, marker) => total + (text.includes(marker) ? 1 : 0), 0);
        return { element, textLength: text.length, score };
      })
      .filter((candidate) => candidate.score >= 2)
      .sort((a, b) => b.score - a.score || a.textLength - b.textLength);

    for (const candidate of candidates) {
      const root = this.getDirectFooterChild(candidate.element, footer);
      if (root && this.isEligibleWidgetRoot(root)) return root;
    }

    return null;
  }

  getDirectFooterChild(element, footer) {
    if (!element || !footer.contains(element)) return null;

    let root = element;
    while (
      root.parentElement &&
      root.parentElement !== footer &&
      !root.parentElement.matches('.cart__ctas')
    ) {
      root = root.parentElement;
    }

    if (root.parentElement === footer || root.parentElement?.matches('.cart__ctas')) return root;
    return null;
  }

  isEligibleWidgetRoot(element) {
    if (!element) return false;
    return !element.matches(
      '.irie-cart-summary, .irie-cart-delivery-intro, .cart__ctas, details, script, style, noscript'
    );
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
